import { baseApi } from "@/shared/api";
import type { AuditListResponse } from "@/shared/types/audit";
import { fileResponseHandler, type FileResponse } from "@/shared/utils";
import { decrementPendingActionsCount } from "@/features/auth";
import { authApi } from "@/features/auth/api";
import { pendingActionsApi } from "@/features/self/pending/api/pendingActions";
import type {
  SelfResignationCreateRequest,
  SelfResignationCreateResponse,
  AcknowledgeResponse,
  ResignationLetterResponse,
} from "@/features/hr/resignation-letters";
import type {
  AnnualLeaveResponse,
  UnpaidLeaveResponse,
  MedicalLeaveResponse,
  UpdateAnnualLeaveDto,
  UpdateUnpaidLeaveDto,
  UpdateMedicalLeaveDto,
} from "@/features/hr/leave-applications";
import type { SelfServiceRequest } from "../types";

export interface SalaryAdvanceCreateBody {
  amount_mode: "exact" | "percent";
  amount?: number;
  percent?: string;
  advance_month: number;
  advance_year: number;
  document_date: string;
}

export interface SelfLeaveCreateRequest {
  start_date: string;
  end_date: string;
  reason?: string;
  diagnosis?: string;
  approval_resolution?: "approved" | "recommend" | "no_objection";
}

export interface SelfLeaveCreateResponse {
  id: number;
  status: "draft";
  leave_type: "annual" | "medical" | "unpaid";
  worker_id: number;
  start_date: string;
  end_date: string;
  created_at: string;
}

const REQUEST_TYPE_TO_TARGET_TYPE: Record<string, string> = {
  annual_leave: "annual_leave",
  unpaid_leave: "unpaid_leave",
  medical_leave: "medical_leave",
  amendment: "contractamendment",
  resignation: "resignation_letter",
  completion_act: "completionact",
  unit_request: "unitrequest",
  data_change_request: "personaldatachangerequest",
  advance: "advancerequest",
};

export const requestsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getSelfRequests: builder.query<SelfServiceRequest[], { status?: string; type?: string } | void>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params && typeof params === "object") {
          if (params.status) searchParams.append("status", params.status);
          if (params.type) searchParams.append("type", params.type);
        }
        const queryString = searchParams.toString();
        return {
          url: `/api/self/requests/${queryString ? `?${queryString}` : ""}`,
          method: "GET",
          credentials: "include",
        };
      },
      transformResponse: (response: SelfServiceRequest[] | { results: SelfServiceRequest[] }) => {
        if (Array.isArray(response)) return response;
        return response.results ?? [];
      },
      providesTags: [{ type: "SelfService" as const, id: "REQUESTS" }],
    }),

    createSelfAnnualLeave: builder.mutation<SelfLeaveCreateResponse, Omit<SelfLeaveCreateRequest, "diagnosis" | "approval_resolution">>({
      query: (body) => ({
        url: "/api/self/leaves/annual/create/",
        method: "POST",
        body,
        credentials: "include",
      }),
      invalidatesTags: [{ type: "SelfService" as const, id: "REQUESTS" }, "PendingActions"],
    }),

    createSelfMedicalLeave: builder.mutation<SelfLeaveCreateResponse, Omit<SelfLeaveCreateRequest, "reason" | "approval_resolution">>({
      query: (body) => ({
        url: "/api/self/leaves/medical/create/",
        method: "POST",
        body,
        credentials: "include",
      }),
      invalidatesTags: [{ type: "SelfService" as const, id: "REQUESTS" }, "PendingActions"],
    }),

    createSelfUnpaidLeave: builder.mutation<SelfLeaveCreateResponse, Omit<SelfLeaveCreateRequest, "diagnosis">>({
      query: (body) => ({
        url: "/api/self/leaves/unpaid/create/",
        method: "POST",
        body,
        credentials: "include",
      }),
      invalidatesTags: [{ type: "SelfService" as const, id: "REQUESTS" }, "PendingActions"],
    }),

    acknowledgeAnnualOrder: builder.mutation<
      { message: string; acknowledged: boolean; acknowledged_at: string },
      number
    >({
      query: (id) => ({
        url: `/api/self/leaves/annual/${id}/acknowledge-order/`,
        method: "POST",
        credentials: "include",
      }),
      invalidatesTags: [
        { type: "SelfService" as const, id: "REQUESTS" },
        "PendingActions",
      ],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(decrementPendingActionsCount());
      },
    }),

    acknowledgeUnpaidOrder: builder.mutation<
      { message: string; acknowledged: boolean; acknowledged_at: string },
      number
    >({
      query: (id) => ({
        url: `/api/self/leaves/unpaid/${id}/acknowledge-order/`,
        method: "POST",
        credentials: "include",
      }),
      invalidatesTags: [
        { type: "SelfService" as const, id: "REQUESTS" },
        "PendingActions",
      ],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(decrementPendingActionsCount());
      },
    }),

    acknowledgeMedicalOrder: builder.mutation<
      { message: string; acknowledged: boolean; acknowledged_at: string },
      number
    >({
      query: (id) => ({
        url: `/api/self/leaves/medical/${id}/acknowledge-order/`,
        method: "POST",
        credentials: "include",
      }),
      invalidatesTags: [
        { type: "SelfService" as const, id: "REQUESTS" },
        "PendingActions",
      ],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(decrementPendingActionsCount());
      },
    }),

    createSelfResignation: builder.mutation<SelfResignationCreateResponse, SelfResignationCreateRequest>({
      query: (body) => ({
        url: "/api/self/resignation/create/",
        method: "POST",
        body,
        credentials: "include",
      }),
      invalidatesTags: [{ type: "SelfService" as const, id: "REQUESTS" }, "PendingActions"],
    }),

    acknowledgeResignationOrder: builder.mutation<AcknowledgeResponse, number>({
      query: (id) => ({
        url: `/api/self/resignation/${id}/acknowledge-order/`,
        method: "POST",
        credentials: "include",
      }),
      invalidatesTags: [
        { type: "SelfService" as const, id: "REQUESTS" },
        "PendingActions",
      ],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(decrementPendingActionsCount());
      },
    }),

    acknowledgeBusinessTripOrder: builder.mutation<AcknowledgeResponse, number>({
      query: (id) => ({
        url: `/api/self/business-trips/${id}/acknowledge-order/`,
        method: "POST",
        credentials: "include",
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "SelfService" as const, id: "REQUESTS" },
        { type: "BusinessTrips" as const, id },
        { type: "BusinessTrips" as const, id: "LIST" },
        "PendingActions",
      ],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(decrementPendingActionsCount());
      },
    }),

    updateSelfAnnualLeave: builder.mutation<AnnualLeaveResponse, { id: number; data: UpdateAnnualLeaveDto }>({
      query({ id, data }) {
        return {
          url: `/api/hr/annual-leaves/${id}/`,
          method: "PATCH",
          body: data,
          credentials: "include",
        };
      },
      invalidatesTags(_result, _error, { id }) {
        return [
          { type: "SelfService" as const, id: "REQUESTS" },
          { type: "LeaveApplications" as const, id },
          { type: "LeaveApplications" as const, id: "LIST" },
          "PendingActions",
        ];
      },
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        const pendingListPatch = dispatch(
          pendingActionsApi.util.updateQueryData("getPendingActions", undefined, (draft) => {
            const idx = draft.items.findIndex(
              (item) => item.action_required === "revise_application" && item.id === id,
            );
            if (idx !== -1) {
              draft.items.splice(idx, 1);
              draft.summary.total_count = Math.max(0, draft.summary.total_count - 1);
            }
          }),
        );
        const pendingCountPatch = dispatch(
          authApi.util.updateQueryData("getPendingCount", undefined, (draft) => {
            if (draft.count > 0) draft.count -= 1;
          }),
        );
        try {
          await queryFulfilled;
          dispatch(decrementPendingActionsCount());
        } catch {
          pendingListPatch.undo();
          pendingCountPatch.undo();
        }
      },
    }),

    updateSelfUnpaidLeave: builder.mutation<UnpaidLeaveResponse, { id: number; data: UpdateUnpaidLeaveDto }>({
      query({ id, data }) {
        return {
          url: `/api/hr/unpaid-leaves/${id}/`,
          method: "PATCH",
          body: data,
          credentials: "include",
        };
      },
      invalidatesTags(_result, _error, { id }) {
        return [
          { type: "SelfService" as const, id: "REQUESTS" },
          { type: "LeaveApplications" as const, id },
          { type: "LeaveApplications" as const, id: "LIST" },
          "PendingActions",
        ];
      },
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        const pendingListPatch = dispatch(
          pendingActionsApi.util.updateQueryData("getPendingActions", undefined, (draft) => {
            const idx = draft.items.findIndex(
              (item) => item.action_required === "revise_application" && item.id === id,
            );
            if (idx !== -1) {
              draft.items.splice(idx, 1);
              draft.summary.total_count = Math.max(0, draft.summary.total_count - 1);
            }
          }),
        );
        const pendingCountPatch = dispatch(
          authApi.util.updateQueryData("getPendingCount", undefined, (draft) => {
            if (draft.count > 0) draft.count -= 1;
          }),
        );
        try {
          await queryFulfilled;
          dispatch(decrementPendingActionsCount());
        } catch {
          pendingListPatch.undo();
          pendingCountPatch.undo();
        }
      },
    }),

    updateSelfMedicalLeave: builder.mutation<MedicalLeaveResponse, { id: number; data: UpdateMedicalLeaveDto }>({
      query({ id, data }) {
        return {
          url: `/api/hr/medical-leaves/${id}/`,
          method: "PATCH",
          body: data,
          credentials: "include",
        };
      },
      invalidatesTags(_result, _error, { id }) {
        return [
          { type: "SelfService" as const, id: "REQUESTS" },
          { type: "LeaveApplications" as const, id },
          { type: "LeaveApplications" as const, id: "LIST" },
          "PendingActions",
        ];
      },
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        const pendingListPatch = dispatch(
          pendingActionsApi.util.updateQueryData("getPendingActions", undefined, (draft) => {
            const idx = draft.items.findIndex(
              (item) => item.action_required === "revise_application" && item.id === id,
            );
            if (idx !== -1) {
              draft.items.splice(idx, 1);
              draft.summary.total_count = Math.max(0, draft.summary.total_count - 1);
            }
          }),
        );
        const pendingCountPatch = dispatch(
          authApi.util.updateQueryData("getPendingCount", undefined, (draft) => {
            if (draft.count > 0) draft.count -= 1;
          }),
        );
        try {
          await queryFulfilled;
          dispatch(decrementPendingActionsCount());
        } catch {
          pendingListPatch.undo();
          pendingCountPatch.undo();
        }
      },
    }),

    updateSelfResignation: builder.mutation<
      ResignationLetterResponse,
      { id: number; data: { last_working_day: string } }
    >({
      query({ id, data }) {
        return {
          url: `/api/self/resignation/${id}/`,
          method: "PATCH",
          body: data,
          credentials: "include",
        };
      },
      invalidatesTags: [
        { type: "SelfService" as const, id: "REQUESTS" },
        { type: "ResignationLetters" as const, id: "LIST" },
        "PendingActions",
      ],
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        const pendingListPatch = dispatch(
          pendingActionsApi.util.updateQueryData("getPendingActions", undefined, (draft) => {
            const idx = draft.items.findIndex(
              (item) => item.action_required === "revise_application" && item.id === id,
            );
            if (idx !== -1) {
              draft.items.splice(idx, 1);
              draft.summary.total_count = Math.max(0, draft.summary.total_count - 1);
            }
          }),
        );
        const pendingCountPatch = dispatch(
          authApi.util.updateQueryData("getPendingCount", undefined, (draft) => {
            if (draft.count > 0) draft.count -= 1;
          }),
        );
        try {
          await queryFulfilled;
          dispatch(decrementPendingActionsCount());
        } catch {
          pendingListPatch.undo();
          pendingCountPatch.undo();
        }
      },
    }),

    deleteSelfResignation: builder.mutation<void, number>({
      query(id) {
        return {
          url: `/api/self/resignation/${id}/`,
          method: "DELETE",
          credentials: "include",
        };
      },
      invalidatesTags: [
        { type: "SelfService" as const, id: "REQUESTS" },
        { type: "ResignationLetters" as const, id: "LIST" },
      ],
    }),

    getRequestAudit: builder.query<AuditListResponse, { requestType: string; requestId: number }>({
      query: ({ requestType, requestId }) => {
        const targetType = REQUEST_TYPE_TO_TARGET_TYPE[requestType] ?? requestType;
        return {
          url: `/api/audit/?target_type=${targetType}&target_id=${requestId}&page_size=50`,
          method: "GET",
          credentials: "include" as const,
        };
      },
      providesTags: (_r, _e, { requestType, requestId }) => [
        { type: "AuditEntries" as const, id: `${requestType}-${requestId}` },
      ],
      keepUnusedDataFor: 0,
    }),

    createSelfAdvance: builder.mutation<{ id: number; kind: string; status: string }, FormData | Record<string, unknown>>({
      query: (body) => ({
        url: "/api/self/advance/create/",
        method: "POST",
        body,
        credentials: "include" as const,
      }),
      invalidatesTags: [{ type: "SelfService" as const, id: "REQUESTS" }, "PendingActions"],
    }),

    previewSalaryAdvance: builder.mutation<FileResponse, SalaryAdvanceCreateBody>({
      query: (body) => ({
        url: "/api/self/advance/salary/preview/",
        method: "POST",
        body,
        responseHandler: fileResponseHandler,
        credentials: "include" as const,
      }),
    }),

    createAndSignSalaryAdvance: builder.mutation<
      { id: number; status: string; signing_document_id: string; source_type: string },
      SalaryAdvanceCreateBody & { cms_signature: string; file_base64: string }
    >({
      query: (body) => ({
        url: "/api/self/advance/salary/create-and-sign/",
        method: "POST",
        body,
        credentials: "include" as const,
      }),
      invalidatesTags: [{ type: "SelfService" as const, id: "REQUESTS" }, "PendingActions"],
    }),

    deleteSelfAdvance: builder.mutation<void, number>({
      query: (id) => ({
        url: `/api/self/advance/${id}/`,
        method: "DELETE",
        credentials: "include" as const,
      }),
      invalidatesTags: [{ type: "SelfService" as const, id: "REQUESTS" }, "PendingActions"],
    }),

    resubmitSelfAdvance: builder.mutation<{ id: number; status: string }, { id: number; body: FormData }>({
      query: ({ id, body }) => ({
        url: `/api/self/advance/${id}/`,
        method: "PATCH",
        body,
        credentials: "include" as const,
      }),
      invalidatesTags: [{ type: "SelfService" as const, id: "REQUESTS" }, "PendingActions"],
    }),
  }),
});

export const {
  useGetSelfRequestsQuery,
  useCreateSelfAnnualLeaveMutation,
  useCreateSelfMedicalLeaveMutation,
  useCreateSelfUnpaidLeaveMutation,
  useAcknowledgeAnnualOrderMutation,
  useAcknowledgeUnpaidOrderMutation,
  useAcknowledgeMedicalOrderMutation,
  useCreateSelfResignationMutation,
  useAcknowledgeResignationOrderMutation,
  useAcknowledgeBusinessTripOrderMutation,
  useGetRequestAuditQuery,
  useUpdateSelfAnnualLeaveMutation,
  useUpdateSelfUnpaidLeaveMutation,
  useUpdateSelfMedicalLeaveMutation,
  useUpdateSelfResignationMutation,
  useDeleteSelfResignationMutation,
  useCreateSelfAdvanceMutation,
  usePreviewSalaryAdvanceMutation,
  useCreateAndSignSalaryAdvanceMutation,
  useDeleteSelfAdvanceMutation,
  useResubmitSelfAdvanceMutation,
} = requestsApi;

